import * as Sentry from '@sentry/react';

/**
 * Sentry Configuration
 * 
 * Monitors frontend errors, performance, and user sessions.
 * Integrates with error boundaries and provides detailed error tracking.
 */

export function initSentry() {
  // Only initialize in production or when DSN is configured
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  
  if (!dsn) {
    console.warn('[Sentry] DSN not configured. Error tracking disabled.');
    return;
  }

  Sentry.init({
    dsn,
    
    // Environment and release tracking
    environment: import.meta.env.MODE || 'production',
    release: import.meta.env.VITE_GIT_SHA || 'unknown',
    
    // Integrations
    integrations: [
      // Browser tracing for performance monitoring
      Sentry.browserTracingIntegration(),
      
      // Replay sessions for debugging
      Sentry.replayIntegration({
        maskAllText: true, // Privacy: mask all text content
        blockAllMedia: true, // Privacy: block all media
        networkDetailAllowUrls: [
          /^https:\/\/.*\.supabase\.co/,
        ],
      }),
      
      // Browser profiling
      Sentry.browserProfilingIntegration(),
    ],

    // Performance Monitoring
    tracesSampleRate: 0.2, // 20% of transactions (adjust based on traffic)
    
    // Session Replay (for debugging)
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

    // Profiling
    profilesSampleRate: 0.1, // 10% of transactions

    // Error Filtering
    beforeSend(event, hint) {
      // Filter out noisy errors
      const error = hint.originalException;
      
      // Ignore ResizeObserver errors (common browser quirk)
      if (error && error.toString().includes('ResizeObserver')) {
        return null;
      }

      // Ignore network errors from ad blockers
      if (error && error.toString().includes('Failed to fetch')) {
        return null;
      }

      // Ignore cancelled requests
      if (error && error.toString().includes('AbortError')) {
        return null;
      }

      // Add user context if available
      const userEmail = localStorage.getItem('user_email');
      if (userEmail) {
        event.user = {
          ...event.user,
          email: userEmail,
        };
      }

      return event;
    },

    // Breadcrumbs
    beforeBreadcrumb(breadcrumb, hint) {
      // Don't log console.log breadcrumbs in production
      if (breadcrumb.category === 'console' && import.meta.env.PROD) {
        return null;
      }

      // Filter out sensitive data from breadcrumbs
      if (breadcrumb.data?.url) {
        // Remove query parameters that might contain sensitive data
        breadcrumb.data.url = breadcrumb.data.url.split('?')[0];
      }

      return breadcrumb;
    },

    // Ignore certain errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'chrome-extension://',
      'moz-extension://',
      
      // Network errors
      'NetworkError',
      'Network request failed',
      
      // ResizeObserver
      'ResizeObserver loop',
      
      // Random plugins
      'atomicFindClose',
      'conduitPage',
      
      // Facebook quirks
      'fb_xd_fragment',
      
      // Other browser quirks
      'NS_ERROR_NOT_INITIALIZED',
      'NS_ERROR_FAILURE',
    ],
  });

  // Set initial user context
  const userId = localStorage.getItem('user_id');
  if (userId) {
    Sentry.setUser({ id: userId });
  }

  console.log('[Sentry] Initialized successfully');
}

/**
 * Set user context for Sentry
 */
export function setSentryUser(user: { id: string; email?: string; username?: string }) {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.username,
  });
}

/**
 * Clear user context (on logout)
 */
export function clearSentryUser() {
  Sentry.setUser(null);
}

/**
 * Capture exception manually
 */
export function captureException(error: Error, context?: Record<string, any>) {
  Sentry.captureException(error, {
    contexts: context,
  });
}

/**
 * Add breadcrumb manually
 */
export function addBreadcrumb(message: string, data?: Record<string, any>) {
  Sentry.addBreadcrumb({
    message,
    data,
    level: 'info',
  });
}

/**
 * Measure performance of a function
 */
export async function measurePerformance<T>(
  name: string,
  fn: () => Promise<T>
): Promise<T> {
  return await Sentry.startSpan(
    {
      name,
      op: 'function',
    },
    async () => {
      return await fn();
    }
  );
}
